import { createContext, type PropsWithChildren, useCallback, useMemo } from 'react';
import { type StoreApi } from 'zustand';

import { useRegisterOcBetslipCallbacks } from '@cx-modules/hooks';

import { type MatchesCouponState, type SyncBet } from '../interfaces/matches-coupon.interface';
import { createMatchesCouponStore } from '../MatchesCoupon.store';
import { type MatchesCouponProps } from '../types';

export const MatchesCouponContext = createContext<StoreApi<MatchesCouponState> | null>(null);

export const MatchesCouponProvider = ({ children, ...props }: PropsWithChildren<MatchesCouponProps>) => {
  const store = useMemo(() => createMatchesCouponStore(props), [props]);

  const syncBetslip = useCallback(
    (bets: SyncBet[], overwriteSelections?: boolean) => {
      store.getState().syncBetslip?.(bets, overwriteSelections);
    },
    [store],
  );

  const removeAllSelectedBets = useCallback(() => {
    store.getState().removeAllSelectedBets?.();
  }, [store]);

  const removeSelectedBet = useCallback(
    (betIdOrObj: number | { betId: number }) => {
      // UK betslip can pass either a number or { betId: number }
      const betId = typeof betIdOrObj === 'number' ? betIdOrObj : betIdOrObj.betId;

      store.getState().removeSelectedBet?.(betId);
    },
    [store],
  );

  // Register callbacks with UK betslip using the helper
  useRegisterOcBetslipCallbacks(
    {
      clearBetslip: removeAllSelectedBets,
      removeAllBets: removeAllSelectedBets,
      removeBet: removeSelectedBet,
      syncBetslip: syncBetslip,
    },
    [removeAllSelectedBets, removeSelectedBet, syncBetslip],
  );

  return <MatchesCouponContext.Provider value={store}>{children}</MatchesCouponContext.Provider>;
};
